import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Mail, Download, PenTool, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { menusAPI, settingsAPI, emailAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import html2pdf from 'html2pdf.js';
import SignatureCanvas from 'react-signature-canvas';

const sortMenuItems = (items) => {
  if (!items || items.length === 0) return [];
  const categoryOrder = {
    'salad': 1, 'salads': 1, 'soup': 2, 'soups': 2,
    'appetizer': 3, 'appetizers': 3, 'starter': 3, 'starters': 3,
    'mains': 4, 'main': 4, 'entree': 4, 'entrees': 4, 'protein': 4,
    'sides': 5, 'side': 5, 'vegetable': 5, 'vegetables': 5, 'starch': 5,
    'dessert': 6, 'desserts': 6, 'beverage': 7, 'beverages': 7, 'coffee': 7, 'drinks': 7
  };
  return [...items].sort((a, b) => {
    const catA = (a.category || '').toLowerCase();
    const catB = (b.category || '').toLowerCase();
    return (categoryOrder[catA] || 99) - (categoryOrder[catB] || 99);
  });
};

const groupItemsByCategory = (items) => {
  const groups = {};
  (items || []).forEach(item => {
    const cat = item.category || 'Other';
    const label = cat.charAt(0).toUpperCase() + cat.slice(1);
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  });
  return groups;
};

const ContractPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contract = location.state?.contract;
  const [selectedMenusData, setSelectedMenusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSignature, setClientSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [terms, setTerms] = useState(null);
  const contentRef = useRef(null);
  const sigCanvas = useRef(null);
  const { toast } = useToast();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', body: '' });

  useEffect(() => {
    if (!contract) { navigate('/contracts'); return; }
    setSelectedMenusData([]);
    setLoading(true);
    setClientSignature(null);
    fetchData();
  }, [contract?.id, navigate]);

  const fetchData = async () => {
    try {
      const [allMenus, termsData] = await Promise.all([menusAPI.getAll(), settingsAPI.getContractTerms()]);
      if (contract.selected_menus_details?.length > 0) {
        setSelectedMenusData(contract.selected_menus_details);
      } else {
        setSelectedMenusData(allMenus.filter(m => contract.selected_menus?.includes(m.id)));
      }
      setTerms(termsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = () => {
    html2pdf().set({
      margin: 0.4, filename: `Contract_${contract.contract_number || contract.event_name}_${contract.event_date}.pdf`,
      image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    }).from(contentRef.current).save();
    toast({ title: 'Contract PDF Downloaded!' });
  };

  const clearSignature = () => sigCanvas.current?.clear();

  const saveSignature = () => {
    if (sigCanvas.current?.isEmpty()) { toast({ title: 'Please provide a signature', variant: 'destructive' }); return; }
    setClientSignature(sigCanvas.current.toDataURL('image/png'));
    setShowSignatureModal(false);
    toast({ title: 'Signature saved!' });
  };

  const openEmailModal = () => {
    setEmailForm({
      to_email: contract.client_email || '',
      subject: `Catering Contract - ${contract.event_name} (${formatDate(contract.event_date)})`,
      body: `Dear ${contract.client_name},\n\nPlease find attached the catering contract for your upcoming event "${contract.event_name}" scheduled for ${formatDate(contract.event_date)}.\n\nEvent Details:\n- Location: ${contract.location}\n- Guest Count: ${contract.guest_count}\n- Total Amount: $${contract.total_amount?.toFixed(2)}\n\nPlease review the contract and let us know if you have any questions.\n\nBest regards,\n${terms?.company_name || 'Willow Ridge Catering'}`
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email) { toast({ title: 'Please enter recipient email', variant: 'destructive' }); return; }
    setSending(true);
    try {
      await emailAPI.sendContract({ to_email: emailForm.to_email, subject: emailForm.subject, body: emailForm.body, contract_id: contract.id });
      toast({ title: 'Contract sent successfully!', description: `Email sent to ${emailForm.to_email}` });
      setShowEmailModal(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast({ title: errorMsg.includes('SMTP') ? 'Email not configured' : 'Failed to send email', description: errorMsg, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const formatTime = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getActiveTableRequirements = () => {
    const reqs = [];
    if (contract?.dj_table) reqs.push('DJ Table');
    if (contract?.gift_table) reqs.push('Gift Table');
    if (contract?.head_table) reqs.push('Head Table');
    if (contract?.cake_table) reqs.push('Cake Table');
    if (contract?.water_station) reqs.push('Water Station');
    if (contract?.coffee_station) reqs.push('Coffee Station');
    if (contract?.bar_setup) reqs.push('Bar Setup');
    if (contract?.podium) reqs.push('Podium');
    if (contract?.microphone) reqs.push('Microphone');
    return reqs;
  };

  const customAddonsTotal = (contract.custom_addons || []).reduce((sum, a) => sum + (a.total || 0), 0);

  if (loading) return (
    <div className="p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
      <Loader2 className="animate-spin text-[#1e3a5f]" size={32} />
    </div>
  );

  return (
    <div className="p-8 bg-[#faf9f6] min-h-screen print:bg-white print:p-0" data-testid="contract-preview-page">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6 print:hidden max-w-4xl mx-auto">
        <Button onClick={() => navigate('/contracts')} variant="outline" data-testid="back-to-contracts-btn"
          className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
          <ArrowLeft size={18} className="mr-2" /> Back to Contracts
        </Button>
        <div className="flex gap-2">
          <Button onClick={openEmailModal} data-testid="send-to-client-btn" variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
            <Send size={16} className="mr-2" /> Send
          </Button>
          <Button onClick={() => setShowSignatureModal(true)} data-testid="e-sign-btn" variant="outline"
            className="border-[#8b7355] text-[#8b7355] hover:bg-[#8b7355] hover:text-white">
            <PenTool size={16} className="mr-2" /> E-Sign
          </Button>
          <Button onClick={handleDownloadPDF} data-testid="download-contract-pdf-btn"
            className="bg-[#1e3a5f] hover:bg-[#15304f] text-white">
            <Download size={16} className="mr-2" /> PDF
          </Button>
          <Button onClick={handlePrint} data-testid="print-contract-btn"
            className="bg-[#c9a227] hover:bg-[#b08f1f] text-white font-semibold">
            <Printer size={16} className="mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* Modals */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sign Contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Please sign below to agree to the terms and conditions.</p>
            <div className="border-2 border-gray-300 rounded-lg bg-white">
              <SignatureCanvas ref={sigCanvas} canvasProps={{ width: 450, height: 200, className: 'signature-canvas' }} backgroundColor="white" />
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={clearSignature}>Clear</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
                <Button onClick={saveSignature} className="bg-[#1e3a5f] text-white">Save Signature</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={20} /> Send Contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Recipient Email *</Label><Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} data-testid="email-to-input" /></div>
            <div><Label>Subject</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject-input" /></div>
            <div><Label>Message</Label><Textarea value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} rows={6} data-testid="email-body-input" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
              <Button onClick={handleSendEmail} disabled={sending} className="bg-blue-600 text-white" data-testid="send-email-btn">
                {sending ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</> : <><Send size={16} className="mr-2" /> Send</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Document */}
      <div ref={contentRef}>
        <div className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none print:max-w-full" style={{ fontFamily: "'Georgia', serif" }}>
          {/* Header */}
          <div className="bg-[#1e3a5f] text-white py-10 px-10 print:py-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold tracking-wide">{terms?.company_name || 'WILLOW RIDGE'}</h1>
                <p className="text-sm opacity-80 mt-1">{terms?.company_tagline || 'Catering & Events'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-60 uppercase tracking-wider">Contract</p>
                <p className="text-sm font-mono mt-1">{contract.contract_number}</p>
                <p className="text-xs opacity-60 mt-2">{new Date().toLocaleDateString()}</p>
              </div>
            </div>
            <div className="border-t border-white/20 mt-6 pt-4">
              <h2 className="text-xl font-light tracking-wide">CATERING SERVICE AGREEMENT</h2>
            </div>
          </div>

          <div className="px-10 py-8 print:px-8 print:py-6 space-y-8">
            {/* Parties */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-3">Service Provider</p>
                <p className="font-semibold text-[#1e3a5f]">{terms?.company_name || 'Willow Ridge Golf Club'}</p>
                <p className="text-sm text-gray-500 whitespace-pre-line mt-1">{terms?.company_address || '123 Willow Ridge Drive\nGolf City, ON L0L 1L0'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-3">Client</p>
                <p className="font-semibold text-[#1e3a5f]">{contract.client_name}</p>
                {contract.secondary_client_name && <p className="text-sm text-[#1e3a5f]">& {contract.secondary_client_name}</p>}
                <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                  <p>{contract.client_email}</p>
                  <p>{contract.client_phone}</p>
                  {contract.billing_address && <p className="mt-1">{contract.billing_address}</p>}
                </div>
              </div>
            </div>

            <div className="border-t border-[#c9a227]" />

            {/* Event Details */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Event Details</h3>
              <div className="grid grid-cols-3 gap-y-4 gap-x-8">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Event</p>
                  <p className="font-semibold text-[#1e3a5f]">{contract.event_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Type</p>
                  <p className="text-[#1e3a5f] capitalize">{contract.event_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Guests</p>
                  <p className="text-[#1e3a5f] font-semibold">{contract.guest_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Date</p>
                  <p className="text-[#1e3a5f]">{formatDate(contract.event_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Time</p>
                  <p className="text-[#1e3a5f]">{formatTime(contract.event_time) || contract.event_time}</p>
                </div>
                {contract.service_style && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Service Style</p>
                    <p className="text-[#1e3a5f] capitalize">{contract.service_style?.replace(/_/g, ' ')}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-xs text-gray-400 mb-0.5">Location</p>
                  <p className="text-[#1e3a5f]">{contract.location}</p>
                </div>
                {contract.bar_type && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Bar Service</p>
                    <p className="text-[#1e3a5f] capitalize">{contract.bar_type?.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Event Schedule */}
            {(contract.event_start_time || contract.cocktail_hour_time || contract.dinner_start_time) && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Event Schedule</h3>
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { time: contract.event_start_time, label: 'Arrival' },
                    { time: contract.cocktail_hour_time, label: 'Cocktail Hour' },
                    { time: contract.dinner_start_time, label: 'Dinner' },
                    { time: contract.late_night_time, label: 'Late Night' },
                    { time: contract.event_end_time, label: 'Conclusion' },
                  ].filter(t => t.time).map((t) => (
                    <div key={t.label} className="bg-[#faf9f6] rounded p-3 text-center">
                      <p className="text-sm font-semibold text-[#1e3a5f]">{formatTime(t.time)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200" />

            {/* Menu Section - Clean, Professional */}
            {selectedMenusData.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-5">Menu</h3>
                <div className="space-y-6">
                  {selectedMenusData.map((menu) => (
                    <div key={menu.id}>
                      <div className="flex justify-between items-baseline mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-[#1e3a5f]">{menu.name}</h4>
                          {menu.category && <p className="text-xs text-gray-400 uppercase tracking-wide">{menu.category}</p>}
                        </div>
                        {menu.price_per_person > 0 && (
                          <p className="text-sm text-gray-500">${menu.price_per_person}/person</p>
                        )}
                      </div>
                      {menu.items && menu.items.length > 0 && (() => {
                        const grouped = groupItemsByCategory(menu.items);
                        const cats = Object.keys(grouped);
                        if (cats.length === 1 && cats[0] === 'Other') {
                          return (
                            <div className="grid grid-cols-2 gap-x-8 gap-y-1 pl-4">
                              {sortMenuItems(menu.items).map((item) => (
                                <div key={item.id || item.name} className="py-1">
                                  <p className="text-sm text-gray-700">{item.name}</p>
                                  {item.description && <p className="text-xs text-gray-400 italic">{item.description}</p>}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-3 pl-4">
                            {cats.map(cat => (
                              <div key={cat}>
                                <p className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold mb-1">{cat}</p>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                  {grouped[cat].map((item, idx) => (
                                    <div key={idx} className="py-0.5">
                                      <p className="text-sm text-gray-700">{item.name}{item.quantity > 0 ? ` (${item.quantity})` : ''}</p>
                                      {item.description && <p className="text-xs text-gray-400 italic">{item.description}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Menu (legacy single field, used for pickup/dropoff)
                Hide this block for non-pickup/dropoff events when any per-phase
                custom menu is also populated — prevents duplicate cocktail notes. */}
            {contract.custom_menu && (
              (contract.event_type === 'pickup' || contract.event_type === 'dropoff')
              || !(contract.custom_menu_breakfast || contract.custom_menu_lunch || contract.custom_menu_cocktail || contract.custom_menu_dinner || contract.custom_menu_dessert || contract.custom_menu_late_night || contract.custom_menu_bar || contract.custom_menu_beverage || contract.custom_menu_on_the_turn)
            ) && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">
                  {(contract.event_type === 'pickup' || contract.event_type === 'dropoff') ? 'Menu' : 'Custom Menu Notes'}
                </h4>
                <div className="pl-4 whitespace-pre-line text-sm text-gray-700 leading-relaxed">
                  {contract.custom_menu}
                </div>
              </div>
            )}

            {/* Per-phase Custom Menus (Breakfast / Lunch / Cocktail / Dinner / Dessert / Late Night / Bar / Beverage / On the Turn) */}
            {(contract.custom_menu_breakfast || contract.custom_menu_lunch || contract.custom_menu_cocktail || contract.custom_menu_dinner || contract.custom_menu_dessert || contract.custom_menu_late_night || contract.custom_menu_bar || contract.custom_menu_beverage || contract.custom_menu_on_the_turn || (contract.custom_menu_breakfast_tiers || []).length > 0 || (contract.custom_menu_lunch_tiers || []).length > 0 || (contract.custom_menu_cocktail_tiers || []).length > 0 || (contract.custom_menu_dinner_tiers || []).length > 0 || (contract.custom_menu_dessert_tiers || []).length > 0 || (contract.custom_menu_late_night_tiers || []).length > 0 || (contract.custom_menu_bar_tiers || []).length > 0 || (contract.custom_menu_beverage_tiers || []).length > 0 || (contract.custom_menu_on_the_turn_tiers || []).length > 0) && (
              <div className="mb-6 space-y-4">
                {[
                  { label: 'Breakfast', text: contract.custom_menu_breakfast, price: contract.custom_menu_breakfast_price, tiers: contract.custom_menu_breakfast_tiers },
                  { label: 'Lunch', text: contract.custom_menu_lunch, price: contract.custom_menu_lunch_price, tiers: contract.custom_menu_lunch_tiers },
                  { label: "Cocktail Hour / Hors d'Oeuvres", text: contract.custom_menu_cocktail, price: contract.custom_menu_cocktail_price, tiers: contract.custom_menu_cocktail_tiers },
                  { label: 'Dinner Service', text: contract.custom_menu_dinner, price: contract.custom_menu_dinner_price, tiers: contract.custom_menu_dinner_tiers },
                  { label: 'Dessert', text: contract.custom_menu_dessert, price: contract.custom_menu_dessert_price, tiers: contract.custom_menu_dessert_tiers },
                  { label: 'Late Night', text: contract.custom_menu_late_night, price: contract.custom_menu_late_night_price, tiers: contract.custom_menu_late_night_tiers },
                  { label: 'Bar', text: contract.custom_menu_bar, price: contract.custom_menu_bar_price, tiers: contract.custom_menu_bar_tiers },
                  { label: 'Beverage', text: contract.custom_menu_beverage, price: contract.custom_menu_beverage_price, tiers: contract.custom_menu_beverage_tiers },
                  { label: 'On the Turn', text: contract.custom_menu_on_the_turn, price: contract.custom_menu_on_the_turn_price, tiers: contract.custom_menu_on_the_turn_tiers },
                ].filter(p => p.text || (p.tiers || []).length > 0).map((p, idx) => {
                  const guests = contract.guest_count || 0;
                  const validTiers = (p.tiers || []).filter(t => (parseInt(t.quantity) || 0) > 0 && (parseFloat(t.price_per_person) || 0) > 0);
                  const tierTotal = validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
                  const fallbackTotal = (p.price || 0) * guests;
                  const phaseTotal = validTiers.length > 0 ? tierTotal : fallbackTotal;
                  return (
                    <div key={idx}>
                      <div className="flex items-baseline justify-between mb-1">
                        <h4 className="text-sm font-semibold text-[#1e3a5f]">{p.label}</h4>
                        {validTiers.length === 0 && p.price ? (
                          <span className="text-xs text-gray-600">
                            ${p.price.toFixed(2)} / person{guests ? ` × ${guests} = $${fallbackTotal.toFixed(2)}` : ''}
                          </span>
                        ) : null}
                      </div>
                      {p.text && (
                        <div className="pl-4 whitespace-pre-line text-sm text-gray-700 leading-relaxed">
                          {p.text}
                        </div>
                      )}
                      {validTiers.length > 0 && (
                        <table className="ml-4 mt-2 text-sm border-collapse" data-testid={`preview-tiers-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                          <thead>
                            <tr className="text-xs text-gray-500 border-b">
                              <th className="text-left pr-6 py-1 font-normal">Item</th>
                              <th className="text-right px-2 py-1 font-normal">Qty</th>
                              <th className="text-right px-2 py-1 font-normal">$ / Person</th>
                              <th className="text-right pl-4 py-1 font-normal">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validTiers.map((t, i) => {
                              const q = parseInt(t.quantity) || 0;
                              const pr = parseFloat(t.price_per_person) || 0;
                              return (
                                <tr key={i} className="text-sm text-gray-700">
                                  <td className="pr-6 py-1">{t.label || '—'}</td>
                                  <td className="text-right px-2 py-1">{q}</td>
                                  <td className="text-right px-2 py-1">${pr.toFixed(2)}</td>
                                  <td className="text-right pl-4 py-1 font-medium">${(q * pr).toFixed(2)}</td>
                                </tr>
                              );
                            })}
                            <tr className="border-t font-semibold">
                              <td className="pr-6 py-1 text-[#1e3a5f]">Phase Total</td>
                              <td colSpan={2} />
                              <td className="text-right pl-4 py-1 text-[#1e3a5f]">${phaseTotal.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected Items: HD, Buffet, Plated, Late Night, Bar */}
            {(contract.selected_hors_doeuvres?.length > 0 || contract.selected_buffet?.length > 0 || contract.selected_plated_mains?.length > 0 || contract.selected_late_night?.length > 0 || contract.selected_bar?.length > 0) && (
              <div className="space-y-5">
                {contract.selected_hors_doeuvres?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Hors d'Oeuvres</h4>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 pl-4">
                      {contract.selected_hors_doeuvres.map((item, idx) => (
                        <p key={idx} className="text-sm text-gray-700 py-0.5">{item.name}
                          {item.sub_category && <span className="text-xs text-gray-400 ml-1">({item.sub_category})</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {contract.selected_buffet?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Buffet Selections</h4>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 pl-4">
                      {contract.selected_buffet.map((item, idx) => (
                        <p key={idx} className="text-sm text-gray-700 py-0.5">{item.name}</p>
                      ))}
                    </div>
                  </div>
                )}
                {(contract.selected_plated_salad || contract.selected_plated_appetizer || contract.selected_plated_mains?.length > 0 || contract.selected_plated_dessert) && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">{contract.plated_courses || 3}-Course Plated Dinner</h4>
                    <div className="pl-4 space-y-2">
                      {contract.selected_plated_appetizer && (
                        <div className="flex items-baseline gap-3">
                          <span className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold w-20">Appetizer</span>
                          <span className="text-sm text-gray-700">{contract.selected_plated_appetizer.name}</span>
                        </div>
                      )}
                      {contract.selected_plated_salad && (
                        <div className="flex items-baseline gap-3">
                          <span className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold w-20">Salad</span>
                          <span className="text-sm text-gray-700">{contract.selected_plated_salad.name}</span>
                        </div>
                      )}
                      {contract.selected_plated_mains?.map((main, idx) => (
                        <div key={idx} className="flex items-baseline gap-3">
                          <span className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold w-20">{idx === 0 ? 'Mains' : ''}</span>
                          <span className="text-sm text-gray-700">{main.name || main.item?.name}</span>
                          <span className="text-xs text-gray-400 ml-auto">{main.quantity} guests</span>
                        </div>
                      ))}
                      {contract.selected_plated_dessert && (
                        <div className="flex items-baseline gap-3">
                          <span className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold w-20">Dessert</span>
                          <span className="text-sm text-gray-700">{contract.selected_plated_dessert.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {contract.selected_late_night?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Late Night</h4>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 pl-4">
                      {contract.selected_late_night.map((item, idx) => (
                        <div key={idx} className="py-0.5">
                          <p className="text-sm text-gray-700">{item.name}</p>
                          {item.description && <p className="text-xs text-gray-400 italic">{item.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {contract.selected_bar?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Bar Package</h4>
                    <div className="pl-4">
                      {contract.selected_bar.map((item, idx) => (
                        <div key={idx} className="py-0.5">
                          <p className="text-sm text-gray-700">{item.name}</p>
                          {item.description && <p className="text-xs text-gray-400 italic">{item.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add-Ons */}
            {contract.selected_addons?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Add-Ons</h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 pl-4">
                  {contract.selected_addons.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-0.5">
                      <span className="text-sm text-gray-700">{item.name}</span>
                      {item.price_per_person > 0 && <span className="text-xs text-gray-400">${item.price_per_person}/pp</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom/Staff Charges */}
            {contract.custom_addons?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">Staff & Custom Charges</h4>
                <div className="pl-4 space-y-1">
                  {contract.custom_addons.map((addon, idx) => {
                    const mode = addon.pricing_mode || (addon.hours > 0 ? 'hourly' : 'flat');
                    const detail =
                      mode === 'hourly' && addon.hours > 0 ? `${addon.hours} hrs @ $${addon.rate}/hr` :
                      mode === 'per_person' ? `${addon.guest_count_at_add || contract.guest_count} × $${addon.rate}/pp` :
                      null;
                    return (
                      <div key={idx} className="flex justify-between py-0.5">
                        <span className="text-sm text-gray-700">
                          {addon.name}
                          {detail && <span className="text-xs text-gray-400 ml-2">({detail})</span>}
                        </span>
                        <span className="text-sm text-gray-600">${addon.total?.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Setup & Decor */}
            {(contract.table_style || contract.tablecloth_color || getActiveTableRequirements().length > 0 || (contract.selected_rentals?.length > 0)) && (
              <>
                <div className="border-t border-gray-200" />
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Setup & Rentals</h3>
                  <div className="grid grid-cols-2 gap-8">
                    {(contract.table_style || contract.tablecloth_color || contract.napkin_color) && (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Table Setup</p>
                        <div className="text-sm text-gray-700 space-y-1 pl-4">
                          {contract.table_style && <p>Style: <span className="capitalize">{contract.table_style}</span></p>}
                          {contract.tablecloth_color && <p>Tablecloth: {contract.tablecloth_color}</p>}
                          {contract.napkin_color && <p>Napkins: {contract.napkin_color}</p>}
                        </div>
                      </div>
                    )}
                    {getActiveTableRequirements().length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Additional Requirements</p>
                        <div className="text-sm text-gray-700 space-y-0.5 pl-4">
                          {getActiveTableRequirements().map((req, i) => <p key={i}>{req}</p>)}
                        </div>
                      </div>
                    )}
                  </div>
                  {contract.selected_rentals?.length > 0 && (
                    <table className="w-full mt-4 text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-xs text-gray-400 uppercase tracking-wider">
                          <th className="text-left py-2">Rental Item</th>
                          <th className="text-center py-2">Qty</th>
                          <th className="text-center py-2">Color</th>
                          <th className="text-right py-2">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contract.selected_rentals.map((r, idx) => (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="py-2 text-gray-700">{r.name}</td>
                            <td className="py-2 text-center text-gray-600">{r.quantity}</td>
                            <td className="py-2 text-center text-gray-600">{r.color || '—'}</td>
                            <td className="py-2 text-right text-gray-700">${(r.price * r.quantity).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* Special Requests */}
            {contract.special_requests && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-3">Special Requests</h3>
                <p className="text-sm text-gray-700 bg-[#faf9f6] p-4 rounded italic">{contract.special_requests}</p>
              </div>
            )}

            <div className="border-t border-[#c9a227]" />

            {/* Financial Summary */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Financial Summary</h3>
              <div className="max-w-md ml-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {(() => {
                      const guests = contract.guest_count || 0;
                      const phaseTotal = (tiers, price) => {
                        const validTiers = (tiers || []).filter(t => (parseInt(t.quantity) || 0) > 0 && (parseFloat(t.price_per_person) || 0) > 0);
                        if (validTiers.length > 0) {
                          return validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
                        }
                        return (price || 0) * guests;
                      };
                      const customMenuTotal =
                        phaseTotal(contract.custom_menu_breakfast_tiers, contract.custom_menu_breakfast_price)
                        + phaseTotal(contract.custom_menu_lunch_tiers, contract.custom_menu_lunch_price)
                        + phaseTotal(contract.custom_menu_cocktail_tiers, contract.custom_menu_cocktail_price)
                        + phaseTotal(contract.custom_menu_dinner_tiers, contract.custom_menu_dinner_price)
                        + phaseTotal(contract.custom_menu_dessert_tiers, contract.custom_menu_dessert_price)
                        + phaseTotal(contract.custom_menu_late_night_tiers, contract.custom_menu_late_night_price)
                        + phaseTotal(contract.custom_menu_bar_tiers, contract.custom_menu_bar_price)
                        + phaseTotal(contract.custom_menu_beverage_tiers, contract.custom_menu_beverage_price)
                        + phaseTotal(contract.custom_menu_on_the_turn_tiers, contract.custom_menu_on_the_turn_price);
                      const rentalsTotal = contract.selected_rentals?.reduce((s, r) => s + r.price * r.quantity, 0) || 0;
                      const fbTotal = Math.max(0, (contract.subtotal || 0) - rentalsTotal - (contract.plated_subtotal || 0) - (contract.addons_subtotal || 0) - (contract.custom_addons_subtotal || customAddonsTotal) - (contract.travel_fee || 0) - customMenuTotal + (contract.discount_total || 0));
                      return (
                        <>
                    {/* Menu / Food line */}
                    {selectedMenusData.length > 0 && fbTotal > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Food & Beverage <span className="text-xs text-gray-400">({contract.guest_count} guests)</span></td>
                        <td className="py-2 text-right text-gray-700">
                          ${fbTotal.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {customMenuTotal > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Custom Menus</td>
                        <td className="py-2 text-right text-gray-700">${customMenuTotal.toFixed(2)}</td>
                      </tr>
                    )}
                        </>
                      );
                    })()}
                    {contract.plated_subtotal > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Plated Dinner</td>
                        <td className="py-2 text-right text-gray-700">${contract.plated_subtotal?.toFixed(2)}</td>
                      </tr>
                    )}
                    {contract.addons_subtotal > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Add-Ons <span className="text-xs text-gray-400">({contract.selected_addons?.length} items)</span></td>
                        <td className="py-2 text-right text-gray-700">${contract.addons_subtotal?.toFixed(2)}</td>
                      </tr>
                    )}
                    {(contract.custom_addons_subtotal > 0 || customAddonsTotal > 0) && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Staff & Custom Charges</td>
                        <td className="py-2 text-right text-gray-700">${(contract.custom_addons_subtotal || customAddonsTotal).toFixed(2)}</td>
                      </tr>
                    )}
                    {contract.selected_rentals?.length > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Rentals</td>
                        <td className="py-2 text-right text-gray-700">${contract.selected_rentals.reduce((s, r) => s + r.price * r.quantity, 0).toFixed(2)}</td>
                      </tr>
                    )}
                    {contract.travel_fee > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Travel Fee <span className="text-xs text-gray-400">({contract.travel_chargeable_km} km)</span></td>
                        <td className="py-2 text-right text-gray-700">${contract.travel_fee?.toFixed(2)}</td>
                      </tr>
                    )}
                    {contract.discount_total > 0 && (
                      <tr className="border-b border-gray-100 bg-green-50/50">
                        <td className="py-2 text-green-700">
                          Multi-Service Discount
                          {contract.discounts?.map((d, i) => (
                            <span key={i} className="text-xs text-green-500 block">{d.name} ({d.type === 'percentage' ? `${d.value}%` : `$${Number(d.value).toFixed(2)}`})</span>
                          ))}
                        </td>
                        <td className="py-2 text-right text-green-700 font-semibold">-${contract.discount_total?.toFixed(2)}</td>
                      </tr>
                    )}
                    <tr className="border-b border-gray-300">
                      <td className="py-2 font-semibold text-gray-700">Subtotal</td>
                      <td className="py-2 text-right font-semibold text-gray-700">${contract.subtotal?.toFixed(2)}</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 text-gray-600">HST <span className="text-xs text-gray-400">({((contract.tax_rate || 0.13) * 100).toFixed(0)}%)</span></td>
                      <td className="py-2 text-right text-gray-700">${contract.tax_amount?.toFixed(2)}</td>
                    </tr>
                    {contract.gratuity_amount > 0 && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2 text-gray-600">Service Gratuity <span className="text-xs text-gray-400">({((contract.gratuity_rate || 0) * 100).toFixed(0)}%)</span></td>
                        <td className="py-2 text-right text-gray-700">${contract.gratuity_amount?.toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="bg-[#1e3a5f] text-white flex justify-between items-center p-4 rounded mt-2">
                  <span className="font-bold text-lg">TOTAL</span>
                  <span className="font-bold text-lg">${contract.total_amount?.toFixed(2)}</span>
                </div>
                <div className="mt-3 bg-[#faf9f6] p-3 rounded text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Deposit Required (25%)</span>
                    <span className="font-semibold text-[#1e3a5f]">${(contract.total_amount * 0.25)?.toFixed(2)}</span>
                  </div>
                  {contract.total_paid > 0 && (
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-500">Amount Paid</span>
                      <span className="font-semibold text-green-700">${contract.total_paid?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between mt-1 border-t border-gray-200 pt-1">
                    <span className="text-gray-500">Balance Due</span>
                    <span className="font-bold text-[#1e3a5f]">${((contract.total_amount || 0) - (contract.total_paid || 0)).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200" />

            {/* Terms & Conditions */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Terms & Conditions</h3>
              <div className="text-xs text-gray-600 space-y-2 leading-relaxed">
                {(() => {
                  const st = terms?.service_terms?.[contract.event_type];
                  const items = [
                    { title: 'Deposit', text: st?.deposit_note || terms?.deposit_note || 'A 25% deposit is required to secure your date. The remaining balance is due 7 days prior to the event.' },
                    { title: 'Guest Count', text: st?.guest_count_note || terms?.guest_count_note || 'Final guest count must be confirmed 72 hours before the event.' },
                    { title: 'Cancellation', text: st?.cancellation_note || terms?.cancellation_note || 'Cancellations made more than 30 days prior receive a full refund minus a $200 administrative fee.' },
                    { title: 'Changes', text: st?.changes_note || terms?.changes_note || 'Menu and setup changes must be communicated at least 14 days before the event.' },
                    { title: 'Liability', text: st?.liability_note || terms?.liability_note || 'Willow Ridge is not responsible for items left at the venue after the event.' },
                  ];
                  if (st?.additional_terms || terms?.additional_terms) items.push({ title: 'Additional', text: st?.additional_terms || terms?.additional_terms });
                  return items.map((item, i) => (
                    <p key={i}><strong>{i + 1}. {item.title}:</strong> {item.text}</p>
                  ));
                })()}
              </div>
            </div>

            {/* Credit Card Authorization — print-only handwriting form, nothing stored electronically */}
            <div className="pt-6 mt-4 border-t border-gray-200 page-break-inside-avoid">
              <h3 className="text-xs uppercase tracking-widest text-[#8b7355] font-semibold mb-4">Credit Card Authorization</h3>
              <div className="text-xs text-gray-600 mb-3 italic">
                Please complete this section to authorize payment. This form will be securely destroyed after processing — no card data is stored electronically.
              </div>

              {/* Card type checkboxes */}
              <div className="flex items-center gap-4 text-sm mb-3" data-testid="cc-card-type">
                <span className="font-semibold text-[#1e3a5f]">Card Type:</span>
                <label className="inline-flex items-center gap-1"><span className="inline-block w-4 h-4 border border-gray-400" />Visa</label>
                <label className="inline-flex items-center gap-1"><span className="inline-block w-4 h-4 border border-gray-400" />Mastercard</label>
                <label className="inline-flex items-center gap-1"><span className="inline-block w-4 h-4 border border-gray-400" />Amex</label>
              </div>

              {/* Cardholder name */}
              <div className="flex items-baseline gap-2 text-sm mb-3">
                <span className="font-semibold text-[#1e3a5f] whitespace-nowrap">Cardholder Name:</span>
                <span className="flex-1 border-b border-gray-400 h-5" />
              </div>

              {/* Card number — 4 groups of boxes */}
              <div className="flex items-baseline gap-2 text-sm mb-3">
                <span className="font-semibold text-[#1e3a5f] whitespace-nowrap">Card Number:</span>
                <div className="flex-1 flex items-center gap-2">
                  {[0, 1, 2, 3].map(g => (
                    <React.Fragment key={g}>
                      <div className="flex gap-1">
                        {[0, 1, 2, 3].map(c => (
                          <span key={c} className="inline-block w-5 h-6 border border-gray-400" />
                        ))}
                      </div>
                      {g < 3 && <span className="text-gray-400">–</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Expiry + CVV + Billing Postal */}
              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-[#1e3a5f] whitespace-nowrap">Expiry:</span>
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-[#1e3a5f] whitespace-nowrap">CVV:</span>
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                    <span className="inline-block w-5 h-6 border border-gray-400" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-[#1e3a5f] whitespace-nowrap">Billing Postal:</span>
                  <span className="flex-1 border-b border-gray-400 h-5" />
                </div>
              </div>

              {/* Payment schedule */}
              {(() => {
                const total = parseFloat(contract.total_amount) || 0;
                const deposit = total * 0.25;
                const balance = total - deposit;
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm mb-4">
                    <div className="font-semibold text-[#1e3a5f] mb-2">Payment Schedule (to be charged to card above)</div>
                    <div className="grid grid-cols-12 gap-2 items-center mb-2">
                      <div className="col-span-3 text-gray-600">Deposit (25%):</div>
                      <div className="col-span-2 font-semibold text-[#1e3a5f]">${deposit.toFixed(2)}</div>
                      <div className="col-span-2 text-xs text-gray-500">Charge Date:</div>
                      <div className="col-span-5 border-b border-gray-400 h-5" />
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3 text-gray-600">Final Balance (75%):</div>
                      <div className="col-span-2 font-semibold text-[#1e3a5f]">${balance.toFixed(2)}</div>
                      <div className="col-span-2 text-xs text-gray-500">Charge Date:</div>
                      <div className="col-span-5 border-b border-gray-400 h-5" />
                    </div>
                    <div className="text-xs text-gray-500 italic mt-2">
                      Amounts above are pre-calculated from the contract total of ${total.toFixed(2)}. Cross out and write in different amounts if a payment plan was agreed upon.
                    </div>
                  </div>
                );
              })()}

              {/* Authorization clause + cardholder signature */}
              <div className="text-xs text-gray-700 leading-relaxed mb-3">
                I, the cardholder named above, authorize {terms?.company_name || 'Willow Ridge'} to charge my credit card for the amounts and dates listed above, in accordance with the terms of this contract.
              </div>
              <div className="grid grid-cols-2 gap-12 mt-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Cardholder Signature</p>
                  <div className="border-b-2 border-gray-300 h-12" />
                  <p className="text-xs text-gray-400 mt-1">Date: _________________</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200" />

            {/* Signatures */}
            <div className="pt-6 mt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-12">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Client Signature</p>
                  {clientSignature ? (
                    <div className="border rounded bg-white p-2 h-16 flex items-center">
                      <img src={clientSignature} alt="Client Signature" className="h-12 object-contain" />
                    </div>
                  ) : (
                    <div onClick={() => setShowSignatureModal(true)}
                      className="border-b-2 border-gray-300 h-16 cursor-pointer hover:bg-gray-50 flex items-end justify-center pb-2 text-gray-300 text-xs print:cursor-default print:hover:bg-transparent">
                      <span className="print:hidden">Click to sign</span>
                    </div>
                  )}
                  <p className="text-sm mt-2 text-[#1e3a5f]">{contract.client_name}</p>
                  <p className="text-xs text-gray-400">Date: {clientSignature ? new Date().toLocaleDateString() : '_________________'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Authorized Representative</p>
                  <div className="border-b-2 border-gray-300 h-16" />
                  <p className="text-sm mt-2 text-[#1e3a5f]">{terms?.company_name || 'Willow Ridge'}</p>
                  <p className="text-xs text-gray-400">Date: _________________</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-400 pt-6 border-t border-gray-100">
              <p>Thank you for choosing {terms?.company_name || 'Willow Ridge'} for your event.</p>
              <p className="mt-0.5">{terms?.company_email || 'events@willowridge.com'} | {terms?.company_phone || '(555) 123-4567'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractPreview;
